import type { StrategyRdChartSeries } from "../strategyRdIndicatorSeries";
import type { ExecutionCandlesOverlaysBundle, TesseraLaneBOverlayId } from "../../types/executionCandlesOverlays";
import type { ExecutionCandlesRegimeOverlay, ExecutionTradeOverlay } from "../../types/trade";

export type TesseraOverlayChartState = {
  /** Epoch-ms bar times aligned with conditions / series after candle prep. */
  barTimesMs: number[];
  regime?: ExecutionCandlesRegimeOverlay | null;
  overlays?: ExecutionCandlesOverlaysBundle | null;
  enabledLaneB: ReadonlySet<TesseraLaneBOverlayId>;
  trades: ExecutionTradeOverlay[];
  selectedTradeId: string | null;
  showPnlBackground: boolean;
  /** Match LWC: drop removed-policy trades from cumulative PnL. */
  policyOverlayActive?: boolean;
  /** Match LWC: use sim_net_pnl when TM overlay is on. */
  simulationLineOverlay?: boolean;
  /**
   * When true, selected-trade position tool shows TV-style stats badges.
   * When false, only the risk/reward boxes + edge lines are drawn.
   */
  showPositionStats?: boolean;
  /** Strategy Lab scan: RR boxes on every match (Trade Review position tool). */
  allPositionTools?: boolean;
  /** Strategy R&D: epoch-ms of the pattern bar to recolor. */
  highlightBarTimeMs?: number | null;
  highlightDirection?: "long" | "short" | "neutral" | null;
  highlightLabel?: string | null;
  /** Strategy R&D Product B′: break / retest / entry markers (preferred over single highlight). */
  pathHighlights?: Array<{
    timeMs: number;
    direction?: "long" | "short" | "neutral" | null;
    label?: string | null;
    showText?: boolean;
    showArrow?: boolean;
    showWash?: boolean;
    emphasis?: "normal" | "strong" | "muted";
    kind?:
      | "setup"
      | "break"
      | "rejection"
      | "retest"
      | "location_armed"
      | "respect"
      | "reject"
      | "break_retest"
      | "entry"
      | "invalid"
      | "marker";
  }> | null;
  /** Strategy R&D applied recipe — entry / stop / target price lines. */
  highlightLevels?: { entry: number; stop: number; target: number } | null;
  /** Strategy Lab: labeled setup / confluence drawings around the example bar. */
  conceptDrawings?: Array<{
    id: string;
    label: string;
    kind: "bar" | "zone" | "level" | "marker" | "ray";
    timeMs: number | null;
    fromTimeMs?: number | null;
    toTimeMs?: number | null;
    high: number | null;
    low: number | null;
    price: number | null;
    fromPrice?: number | null;
    toPrice?: number | null;
    lineExtend?: "none" | "right";
    arrowRight?: boolean;
    showLabel?: boolean;
    markerStyle?: "arrowup" | "arrowdown" | "diamond";
    paneId?: string | null;
    /** Pane the drawing was made for, before moving onto a BE panel / price. */
    sourcePaneId?: string | null;
    /** Lab chart group (recipe chip): `setup`, `conf-N`, `entry`. */
    group?: string | null;
  }> | null;
  /** Strategy Lab: BE indicator-confluence series aligned to `barTimesMs` (§1.6c). */
  rdSeries?: StrategyRdChartSeries[] | null;
  /** Bump to force native-indicator recompute. */
  version: number;
};

const store = new Map<string, TesseraOverlayChartState>();
const listeners = new Map<string, Set<() => void>>();

export function tesseraOverlayStoreKey(
  symbol: string,
  candleType: string,
  namespace: "tradeReview" | "strategyRd" = "tradeReview"
): string {
  const base = `${symbol.trim().toUpperCase()}|${candleType.trim().toLowerCase()}`;
  return namespace === "strategyRd" ? `strategyRd|${base}` : base;
}

export function getTesseraOverlayState(key: string): TesseraOverlayChartState | null {
  return store.get(key) ?? null;
}

export function setTesseraOverlayState(key: string, next: TesseraOverlayChartState): void {
  store.set(key, next);
  const set = listeners.get(key);
  if (set) {
    Array.from(set).forEach((cb) => {
      try {
        cb();
      } catch {
        // ignore listener errors
      }
    });
  }
}

export function subscribeTesseraOverlayState(key: string, cb: () => void): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(cb);
  return () => {
    set?.delete(cb);
    if (set && set.size === 0) listeners.delete(key);
  };
}

/** Collapse consecutive identical labels into [fromMs, toMs) spans. */
export function collapseLabelSpans(
  barTimesMs: readonly number[],
  conditions: readonly (string | null | undefined)[],
  opts?: { skipUnknown?: boolean }
): Array<{ from: number; to: number; label: string }> {
  const skipUnknown = opts?.skipUnknown !== false;
  const spans: Array<{ from: number; to: number; label: string }> = [];
  if (!barTimesMs.length) return spans;

  let i = 0;
  while (i < barTimesMs.length) {
    const raw = conditions[i];
    const label =
      typeof raw === "string" && raw.trim() ? raw.trim().toLowerCase() : "unknown";
    const start = barTimesMs[i]!;
    let j = i + 1;
    while (j < barTimesMs.length) {
      const nextRaw = conditions[j];
      const next =
        typeof nextRaw === "string" && nextRaw.trim()
          ? nextRaw.trim().toLowerCase()
          : "unknown";
      if (next !== label) break;
      j += 1;
    }
    const endExclusive =
      j < barTimesMs.length
        ? barTimesMs[j]!
        : barTimesMs[j - 1]! + Math.max(60_000, (barTimesMs[1] ?? barTimesMs[0]! + 60_000) - barTimesMs[0]!);
    if (!(skipUnknown && (label === "unknown" || label === ""))) {
      spans.push({ from: start, to: endExclusive, label });
    }
    i = j;
  }
  return spans;
}
